import React, { useCallback, useMemo } from 'react';
import { useReactFlow } from 'reactflow';
import styled, { css } from 'styled-components';
import ControlButton, {
  Props as ControlProps,
} from './components/ControlButton';

export type Props = {
  dataTestId?: string;
  isFullScreen?: boolean;
  infoLink: string;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onFullScreen: () => void;
  toggleMiniMap: () => void;
};

const ControlHUD: React.FC<Props> = React.memo(
  ({
    dataTestId,
    isFullScreen = false,
    infoLink,
    onFullScreen,
    onZoomIn,
    onZoomOut,
    toggleMiniMap,
    ...rest
  }) => {
    const { zoomIn, zoomOut } = useReactFlow();

    const onZoomInHandler = useCallback(() => {
      zoomIn?.();
      onZoomIn?.();
    }, [zoomIn, onZoomIn]);

    const onZoomOutHandler = useCallback(() => {
      zoomOut?.();
      onZoomOut?.();
    }, [zoomOut, onZoomOut]);

    const controls: Array<ControlProps> = useMemo(
      () => [
        {
          icon: 'plus',
          tooltipLabel: 'Zoom in',
          onClick: () => onZoomInHandler(),
        },
        {
          icon: 'minus',
          tooltipLabel: 'Zoom uit',

          onClick: () => onZoomOutHandler(),
        },
        {
          icon: isFullScreen ? 'minimize' : 'maximize',
          tooltipLabel: 'Fullscreen',

          onClick: () => onFullScreen(),
        },
        {
          icon: 'map',
          tooltipLabel: 'Kaart',
          onClick: () => toggleMiniMap(),
        },
        {
          icon: 'question-mark',
          tooltipLabel: 'Help',
          link: infoLink,
        },
      ],
      [
        infoLink,
        isFullScreen,
        onFullScreen,
        onZoomInHandler,
        onZoomOutHandler,
        toggleMiniMap,
      ],
    );

    return (
      <Container data-testid={dataTestId} {...rest}>
        {controls.map(control => (
          <ControlButton key={control.tooltipLabel} {...control} />
        ))}
      </Container>
    );
  },
);

const Container = styled.div<{}>(
  ({ theme }) => css`
    border: ${theme.getTokens().border.width.s} solid
      ${theme.color('grey', 'light')};
    border-radius: ${theme.getTokens().border.radius.base};
    background: ${theme.color('white')};
    width: fit-content;
    display: flex;
  `,
);

export default ControlHUD;
